import { z } from 'zod';
import { clientCategorySchema, clientSchema } from './client.js';
import { freeLabelSchema } from '../free-label.js';
/**
 * L'adresse d'un contact référent — **le format est exigé, contrairement au SIRET**.
 *
 * C'est une adresse d'**envoi** : c'est elle qu'on écrit pour une convocation ou un rapport, et
 * une faute ne se voit qu'au message qui ne revient jamais. Exactement l'arbitrage déjà rendu sur
 * `organizationSettingsSchema.contactEmail`, et la même exigence que `personSchema`.
 *
 * ⚠️ **Le champ vide reste `null`, il ne devient pas une erreur** : `freeLabelSchema` replie
 * d'abord, `z.email()` ne juge ensuite que ce qui a été saisi. Sans ce pliage, un formulaire dont
 * l'e-mail n'est pas rempli enverrait `''` et se ferait refuser pour un champ facultatif (M21).
 */
const contactEmailSchema = freeLabelSchema.pipe(z.email().nullable());
/**
 * Ce que le back-office fournit pour **créer un donneur d'ordre** — les quatre cartes de l'écran
 * « Créer un client » : identité, contact référent, adresse.
 *
 * ⚠️ **Le SIRET n'a aucun contrôle de format**, et c'est la décision plutôt qu'un oubli : la
 * maquette l'affiche espacé (« 812 044 190 00027 »), les courriers le donnent collé, et refuser
 * une saisie encore incomplète perdrait les trois cartes remplies au-dessus (M21). C'est un champ
 * d'en-tête de document, pas une clé — même arbitrage que celui de l'organisation.
 *
 * ⚠️ **Aucune unicité sur `name`, et donc aucun refus ici.** Deux communes homonymes existent et
 * un import produit des variantes d'écriture ; la seconde ligne se crée et se **signale** (cf.
 * `createdClientSchema`), elle ne se rejette pas.
 *
 * ⚠️ **Ni `contact` ni `address` ne sont exigés** : `Client.contact_id` et `Client.address_id`
 * sont nullables parce qu'un client naît le plus souvent d'un import qui n'en donne ni l'un ni
 * l'autre. Les rendre obligatoires ici ferait une seconde règle, plus dure que le modèle.
 *
 * ⚠️ **L'adresse est désignée par son code INSEE, pas par un couple « code postal + ville ».**
 * `Address.insee_code` est `NOT NULL` et référence le référentiel des communes (M8) : c'est la
 * première des trois exceptions de M21 — l'intégrité structurelle. L'écran rapproche la saisie du
 * référentiel (`matchMunicipality`) comme le fait déjà l'import d'une demande, et le code postal
 * et la ville de la maquette en **découlent**. Conséquence assumée : un siège social hors du
 * référentiel se crée **sans adresse** plutôt que d'être refusé.
 *
 * ⚠️ **Ni `clientSince`, ni le « client actif » des Options** : ni l'un ni l'autre n'est un champ
 * de ce formulaire dans la maquette, et `Client` n'a pas de colonne d'archivage. Les accepter
 * ici ferait entrer par l'API un état que rien n'affiche.
 *
 * `strictObject` : un champ de trop — un `id` qu'on croirait devoir fournir, une « Fonction » de
 * contact qui n'a pas de colonne — se rejette au lieu de se retirer en silence.
 */
export const createClientSchema = z.strictObject({
    name: z.string().trim().min(1),
    category: clientCategorySchema,
    siret: freeLabelSchema,
    /**
     * Le contact référent (M15), écrit dans `Person` **avec** le client et dans la même
     * transaction. Seul le nom est exigé : `Person.last_name` porte aussi bien « Émilie Rousseau »
     * qu'une raison sociale, et téléphone comme e-mail manquent régulièrement.
     */
    contact: z
        .strictObject({
        lastName: z.string().trim().min(1),
        firstName: freeLabelSchema,
        phone: freeLabelSchema,
        email: contactEmailSchema,
    })
        .nullish()
        .transform((contact) => contact ?? null),
    address: z
        .strictObject({
        inseeCode: z.string().trim().min(1),
        street: freeLabelSchema,
    })
        .nullish()
        .transform((address) => address ?? null),
});
/**
 * Ce que la création rend : le client écrit, **et ce qu'il faut en dire**.
 *
 * ⚠️ **`duplicateNames` est la raison d'être de cette enveloppe**, comme `duplicateReferences`
 * l'est de celle des demandes. Le schéma refuse l'unicité sur `name` et le garde-fou n°3 refuse
 * le blocage : reste la troisième branche, « on enregistre, on **signale**, l'humain tranche ».
 * Le gestionnaire qui vient de taper « Sainte-Luce-sur-Loire » est le seul à savoir s'il crée un
 * homonyme légitime ou un doublon de sa propre main.
 *
 * C'est un **compte, pas un booléen** : « 2 autres clients portent déjà ce nom » se tranche, « un
 * autre client porte déjà ce nom » sous-entend qu'il n'y en a qu'un. `0` est le cas courant, et il
 * ne fait rien afficher.
 */
export const createdClientSchema = z.object({
    client: clientSchema,
    duplicateNames: z.number().int().min(0),
});
//# sourceMappingURL=creation.js.map